import { isFreeBeta } from '../utils/quizAccess';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSyllabus } from '../contexts/SyllabusContext';
import { useQuiz } from '../contexts/QuizContext';
import { useUser } from '../contexts/UserContext';
import { ContentPoint, Substrand, Strand, Stage, SyllabusNavigatorProps } from '../types';
import { Card, CardBody, Button, Grid, Badge } from './BaseComponents';
import { QuizGenerationModal, QuizGenerationParams } from './QuizGenerationModal';

/**
 * True when the backend reports a node has no questions, so it cannot produce a quiz.
 * A missing count is treated as available, so the node stays usable if the count is absent.
 */
const hasNoQuestions = (node: { questionCount?: number }): boolean => node.questionCount === 0;

/** Card styling for a selectable syllabus node, or a greyed-out one when it has no questions. */
const nodeCardClass = (isEmpty: boolean): string => isEmpty
  ? 'opacity-50 cursor-not-allowed bg-neutral-50'
  : 'cursor-pointer hover:border-primary-400 transition-colors';

/**
 * SyllabusNavigator component provides hierarchical navigation through the syllabus
 * Allows users to browse: Year → Stage → Strand → Substrand → ContentPoint
 * With optional quiz generation at each level
 */
export const SyllabusNavigator: React.FC<SyllabusNavigatorProps> = ({
  showQuizGeneration = true,
  onQuizGenerated
}) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showQuizModal, setShowQuizModal] = useState<boolean>(false);
  const [pendingQuizParams, setPendingQuizParams] = useState<{
    startFrom: 'content_point' | 'substrand' | 'strand' | 'stage';
    nodeId: string;
    title?: string;
  } | null>(null);

  const {
    years,
    selectedYear,
    selectedStage,
    selectedStrand,
    selectedSubstrand,
    loadYears,
    selectYear,
    selectStage,
    selectStrand,
    selectSubstrand,
    getBreadcrumbs,
    goBack,
    isLoading: syllabusLoading,
    error: syllabusError
  } = useSyllabus();

  const { generateNewQuiz } = useQuiz();
  const { currentUser, showAuthModal } = useUser();

  useEffect(() => {
    loadYears();
  }, [loadYears]);

  // Auto-select single stage when navigating to a year with only one stage
  useEffect(() => {
    if (selectedYear && !selectedStage && years.length > 0) {
      const currentYear = years.find(y => y.yearNumber === selectedYear);
      if (currentYear && currentYear.stages && currentYear.stages.length === 1) {
        selectStage(currentYear.stages[0]);
      }
    }
  }, [selectedYear, selectedStage, years, selectStage]);

  /**
   * Shows the quiz generation modal for a specific level and node
   */
  const handleShowQuizModal = (
    startFrom: 'content_point' | 'substrand' | 'strand' | 'stage',
    nodeId: string,
    title?: string
  ) => {
    // Check if user is authenticated first
    if (!currentUser) {
      showAuthModal('Please sign up or log in to generate quizzes and track your progress.');
      return;
    }

    setPendingQuizParams({ startFrom, nodeId, title });
    setShowQuizModal(true);
  };

  /**
   * Generates a quiz with the selected parameters
   */
  const handleGenerateQuiz = async (params: QuizGenerationParams) => {
    if (!pendingQuizParams) return;

    try {
      setIsGenerating(true);

      const attemptId = await generateNewQuiz({
        startFrom: pendingQuizParams.startFrom,
        nodeId: pendingQuizParams.nodeId,
        questionCount: params.questionCount,
        difficulty: params.difficulty
      });

      setShowQuizModal(false);
      setPendingQuizParams(null);

      if (onQuizGenerated) {
        onQuizGenerated(attemptId);
      } else {
        navigate(`/quiz/${attemptId}`);
      }
    } catch (error: any) {

      // Check if it's an authentication error
      const isAuthError = error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        error?.message?.toLowerCase().includes('authentication') ||
        error?.message?.toLowerCase().includes('unauthorized');

      if (isAuthError || !currentUser) {
        showAuthModal('Please sign up or log in to generate quizzes and track your progress.');
        setShowQuizModal(false);
        setPendingQuizParams(null);
      } else if (error?.response?.data?.code === 'NO_QUESTIONS') {
        // The node has no questions (e.g. the count was stale); show the server's message
        alert(error.response.data.message);
      } else {
        alert('Failed to generate quiz. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Closes the quiz generation modal
   */
  const handleCloseQuizModal = () => {
    setShowQuizModal(false);
    setPendingQuizParams(null);
  };

  /**
   * Quiz generation chevron component
   */
  const QuizGenerationChevron: React.FC = () => (
    <svg
      className="h-5 w-5 text-neutral-400 group-hover:text-primary-400 transition-colors"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );

  if (syllabusLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-neutral-500">Loading syllabus...</span>
      </div>
    );
  }

  if (syllabusError) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">Error: {syllabusError}</p>
        <Button
          onClick={loadYears}
          variant="outline"
          className="mt-2 text-sm"
        >
          Retry
        </Button>
      </div>
    );
  }

  // Breadcrumb navigation
  const breadcrumbs = getBreadcrumbs();

  return (
    <div>

      {/* Navigation */}
      {breadcrumbs.length > 0 && (
        <div className="mb-6">
          {/* Back Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goBack}
              className="flex items-center text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>

          {/* Breadcrumb Trail */}
          <div className="flex items-center text-sm text-neutral-500">
            <button onClick={() => selectYear('')} className="hover:text-primary-400 transition-colors">
              Home
            </button>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                <span className="mx-2">›</span>
                <button
                  onClick={crumb.onClick}
                  className="hover:text-primary-400 transition-colors max-w-48 truncate"
                >
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}


      {/* Year Selection */}
      {!selectedYear && (
        <div>
          <h3 className="text-lg font-medium text-neutral-900 mb-4">Select Year Level</h3>
          <Grid cols={3} gap="md">
            {years.map((year) => {
              const isActive = year.isEnabled !== false &&
                (!isFreeBeta() || ['7', '8'].includes(String(year.yearNumber)));
              return (
                <button
                  key={year.yearNumber}
                  onClick={isActive ? () => selectYear(year.yearNumber) : undefined}
                  className={`text-left h-full ${isActive ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  disabled={!isActive}
                >
                  <Card
                    className={isActive
                      ? "cursor-pointer hover:border-primary-400 transition-colors"
                      : "opacity-50 cursor-not-allowed bg-neutral-50"
                    }
                    noHover={!isActive}
                  >
                    <CardBody className="min-h-[45px] flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between">
                          <h4 className={`font-semibold ${isActive ? 'text-primary-400' : 'text-neutral-400'}`}>
                            {year.displayName}
                          </h4>
                          {!isActive && <Badge variant={'warning'}>Coming Soon</Badge>}
                        </div>
                      </div>
                      <p className={`text-sm mt-1 ${isActive ? 'text-neutral-600' : 'text-neutral-400'}`}>
                        {year.stages?.length > 1 ? `${year.stages?.length || 0} stages` : ' '}
                      </p>
                    </CardBody>
                  </Card>
                </button>
              );
            })}
          </Grid>
        </div>
      )}

      {/* Stage Selection or Auto-skip Feedback */}
      {selectedYear && !selectedStage && (() => {
        const currentYear = years.find(y => y.yearNumber === selectedYear);
        const hasMultipleStages = currentYear && currentYear.stages && currentYear.stages.length > 1;

        // Show auto-skip loading message for single-stage years
        if (!hasMultipleStages) {
          const singleStage = currentYear?.stages?.[0];
          return (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
              <span className="ml-3 text-neutral-500">
                Selecting {singleStage?.stageName || singleStage?.stageCode}...
              </span>
            </div>
          );
        }

        // Show stage selection for multi-stage years (e.g., Year 10)
        return (
          <div>
            <h3 className="text-lg font-medium text-neutral-900 mb-4">Select Stage</h3>
            <Grid cols={2} gap="md">
              {currentYear.stages.map((stage: Stage) => (
                <button key={stage.stageId} onClick={() => selectStage(stage)} className="text-left">
                  <Card className="cursor-pointer hover:border-primary-400 transition-colors">
                    <CardBody>
                      <h4 className="font-semibold text-primary-400">{stage.stageName || stage.stageCode}</h4>
                      <p className="text-sm text-neutral-600 mt-1">
                        {(stage as any).strandCount || 0} strands
                      </p>
                    </CardBody>
                  </Card>
                </button>
              ))}
            </Grid>
          </div>
        );
      })()}

      {/* Strand Selection */}
      {selectedStage && !selectedStrand && (
        <div>
          <h3 className="text-lg font-medium text-neutral-900 mb-4">Select Strand</h3>
          <div className="space-y-4">
            {selectedStage.strands?.map((strand: Strand) => {
              const isEmpty = hasNoQuestions(strand);
              return (
                <button
                  key={strand.strandId}
                  onClick={isEmpty ? undefined : () => selectStrand(strand)}
                  disabled={isEmpty}
                  className={`text-left w-full ${isEmpty ? 'cursor-not-allowed' : ''}`}
                >
                  <Card className={nodeCardClass(isEmpty)} noHover={isEmpty}>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <h4 className={`font-semibold ${isEmpty ? 'text-neutral-400' : 'text-primary-400'}`}>{strand.strandName}</h4>
                        {isEmpty && <Badge variant={'warning'}>Coming Soon</Badge>}
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">
                        {strand.substrandCount || 0} substrands
                      </p>
                    </CardBody>
                  </Card>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Substrand Selection */}
      {selectedStrand && !selectedSubstrand && (
        <div>
          <h3 className="text-lg font-medium text-neutral-900 mb-4">Select Substrand</h3>
          <div className="space-y-4">
            {selectedStrand.substrands?.map((substrand: Substrand) => {
              const isEmpty = hasNoQuestions(substrand);
              return (
                <button
                  key={substrand.substrandId}
                  onClick={isEmpty ? undefined : () => selectSubstrand(substrand)}
                  disabled={isEmpty}
                  className={`text-left w-full ${isEmpty ? 'cursor-not-allowed' : ''}`}
                >
                  <Card className={nodeCardClass(isEmpty)} noHover={isEmpty}>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <h4 className={`font-semibold ${isEmpty ? 'text-neutral-400' : 'text-primary-400'}`}>{substrand.substrandName}</h4>
                        {isEmpty && <Badge variant={'warning'}>Coming Soon</Badge>}
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">
                        {substrand.contentPointCount || 0} content points
                      </p>
                    </CardBody>
                  </Card>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content Point Selection */}
      {selectedSubstrand && (
        <div>
          <h3 className="text-lg font-medium text-neutral-900 mb-4">Content Points</h3>
          <div className="space-y-4">
            {selectedSubstrand.contentPoints?.map((contentPoint: ContentPoint) => {
              const isEmpty = hasNoQuestions(contentPoint);
              const canGenerate = showQuizGeneration && !isEmpty;
              return (
                <Card
                  key={contentPoint.contentPointId}
                  className={isEmpty
                    ? nodeCardClass(true)
                    : `${canGenerate ? 'cursor-pointer group' : ''} hover:border-primary-400 transition-colors`}
                  noHover={isEmpty}
                  onClick={canGenerate ? () => handleShowQuizModal('content_point', contentPoint.contentPointId, contentPoint.contentDescription) : undefined}
                >
                  <CardBody>
                    <div
                      className="flex items-center justify-between"
                    >
                      <p className={`text-sm font-medium ${isEmpty ? 'text-neutral-400' : 'text-neutral-900'}`}>
                        {contentPoint.contentDescription}
                      </p>
                      {isEmpty && <Badge variant={'warning'}>Coming Soon</Badge>}
                      {canGenerate && (
                        <QuizGenerationChevron />
                      )}
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {years.length === 0 && !syllabusLoading && (
        <div className="text-center py-12">
          <p className="text-neutral-500 mb-4">No syllabus data available.</p>
          <Button onClick={loadYears} variant="primary">
            Reload
          </Button>
        </div>
      )}

      {/* Quiz Generation Modal */}
      <QuizGenerationModal
        isOpen={showQuizModal}
        onClose={handleCloseQuizModal}
        onGenerate={handleGenerateQuiz}
        isGenerating={isGenerating}
        contentTitle={pendingQuizParams?.title ? `Generate Quiz: ${pendingQuizParams.title.substring(0, 40)}${pendingQuizParams.title.length > 40 ? '...' : ''}` : 'Generate Quiz'}
      />
    </div>
  );
};